import { MessageResponse } from '@api/ringil-api.v2';

export type TranslateFn = (key: string, defaultText?: string, data?: Record<string, string>, count?: number) => string;

/**
 * Renders a single backend validation message.
 *
 * A locized message carries a Locize key in `message` plus positional placeholders
 * ({{0}}, {{1}}, …) that are filled from `params`; a non-locized one is already
 * human-readable text produced by the backend and is shown as is.
 */
export const formatBEValidationMessage = (message: MessageResponse, tr: TranslateFn): string => {
  if (message == null) return undefined;
  if (!message.locized) return message.message;
  const params = message.params ?? [];
  return tr(message.message, message.message, Object.fromEntries(params.map((val, idx) => [idx, val])), params.length);
};

/** `reservation.error.slot_occupied`-like: dotted segments, no whitespace. */
const LOCIZE_KEY_SHAPE = /^[A-Za-z0-9_-]+(\.[A-Za-z0-9_-]+)+$/;

/**
 * Renders an async job item failure (RD-2946). The structured `error` is preferred; the plain
 * `errorMessage` fallback covers items that failed before the backend started storing it and,
 * when it is a bare Locize key, still gets translated. The key-shape guard keeps free-text
 * messages away from `tr`, so dev/alpha `saveMissing` never pushes them to Locize as new keys.
 */
export const formatJobItemError = (error: MessageResponse | undefined, errorMessage: string | undefined, tr: TranslateFn): string | undefined => {
  if (error != null) return formatBEValidationMessage(error, tr);
  if (errorMessage == null) return undefined;
  return LOCIZE_KEY_SHAPE.test(errorMessage) ? tr(errorMessage, errorMessage) : errorMessage;
};
